import type { TypedBenefitObject } from "./types";

function parseMonthsIntoResult(
  result: Record<string, unknown>,
  detail: string,
): void {
  const parts = detail.split("/");
  if (parts.length < 2) return;

  const beforeEffDate = parseInt(parts[0], 10);
  const treatmentFree = parseInt(parts[1], 10);

  if (!isNaN(beforeEffDate) && beforeEffDate >= 0) {
    result["monthsDiagnosisFreeBeforeEffDate"] = beforeEffDate;
  }
  if (!isNaN(treatmentFree) && treatmentFree >= 0) {
    result["monthsTreatmentFree"] = treatmentFree;
  }

  if (parts.length === 3) {
    const afterEffDate = parseInt(parts[2], 10);
    if (!isNaN(afterEffDate) && afterEffDate >= 0) {
      result["monthsDiagnosisFreeAfterEffDate"] = afterEffDate;
    }
  }
}

/**
 * Transforms pre-existing condition from grid format (monthsDetail string) to API format.
 *
 * Parses the formatted string (XX/XX or XX/XX/XX) into separate month fields.
 * Month fields are only populated when type is "months".
 *
 * @param typeValue - The pre-existing condition type (e.g., "months", "waived", "none")
 * @param monthsDetail - Formatted months detail string (e.g., "12/06" or "12/06/03")
 * @param defaultType - Default type if missing (default: "none")
 * @param defaultMonthsDetail - Fallback when type is "months" but monthsDetail is empty
 */
export function transformPreExistingConditionToApi(
  typeValue: string | undefined,
  monthsDetail: string | undefined,
  defaultType: string = "none",
  defaultMonthsDetail?: string,
): TypedBenefitObject {
  const type = typeValue ?? defaultType;
  const result: TypedBenefitObject = { type };

  result["monthsDiagnosisFreeBeforeEffDate"] = null;
  result["monthsTreatmentFree"] = null;
  result["monthsDiagnosisFreeAfterEffDate"] = null;

  if (type !== "months") {
    return result;
  }

  let effectiveMonthsDetail = monthsDetail;
  if ((!monthsDetail || monthsDetail === "") && defaultMonthsDetail) {
    effectiveMonthsDetail = defaultMonthsDetail;
  }

  if (effectiveMonthsDetail) {
    parseMonthsIntoResult(result, effectiveMonthsDetail);
  }

  return result;
}

/**
 * Transforms pre-existing condition from API format to grid format (monthsDetail string).
 *
 * Converts separate month fields into a formatted string (XX/XX or XX/XX/XX).
 *
 * @param apiObject - The API object with type and individual month fields
 */
export function transformPreExistingConditionFromApi<
  Type extends string = string,
>(
  apiObject: unknown,
): {
  preExistingConditionType: Type | undefined;
  preExistingConditionMonthsDetail: string;
} {
  const result: {
    preExistingConditionType: Type | undefined;
    preExistingConditionMonthsDetail: string;
  } = {
    preExistingConditionType: undefined,
    preExistingConditionMonthsDetail: "",
  };

  if (apiObject && typeof apiObject === "object") {
    const obj = apiObject as Record<string, unknown>;
    result.preExistingConditionType = obj["type"] as Type | undefined;

    const beforeEffDate = obj["monthsDiagnosisFreeBeforeEffDate"] as
      | number
      | undefined;
    const treatmentFree = obj["monthsTreatmentFree"] as number | undefined;
    const afterEffDate = obj["monthsDiagnosisFreeAfterEffDate"] as
      | number
      | undefined;

    if (
      beforeEffDate !== undefined ||
      treatmentFree !== undefined ||
      afterEffDate !== undefined
    ) {
      const padStart = (value: number | undefined): string => {
        if (value === undefined || value === null) return "00";
        return String(value).padStart(2, "0");
      };

      const before = padStart(beforeEffDate);
      const hasAfter = afterEffDate !== undefined && afterEffDate !== null;
      // A zero treatment-free period reads the same as one that was never
      // extracted at all — collapse both into "before/after" rather than
      // showing a "00" middle segment (CD-1905).
      const hasTreatment =
        treatmentFree !== undefined &&
        treatmentFree !== null &&
        treatmentFree !== 0;

      if (hasAfter && !hasTreatment) {
        result.preExistingConditionMonthsDetail = `${before}/${padStart(afterEffDate)}`;
      } else if (hasAfter) {
        result.preExistingConditionMonthsDetail = `${before}/${padStart(treatmentFree)}/${padStart(afterEffDate)}`;
      } else {
        result.preExistingConditionMonthsDetail = `${before}/${padStart(treatmentFree)}`;
      }
    }
  }

  return result;
}
